document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-leadership-assessment]');
  if (!root) return;

  const questions = [
    ['Communication', 'I communicate expectations clearly and confirm understanding.'],
    ['Communication', 'I listen fully before responding.'],
    ['Communication', 'I adapt my message to the needs of different people.'],
    ['Communication', 'I give timely, useful feedback.'],
    ['Communication', 'I address misunderstandings before they grow.'],
    ['Accountability', 'I follow through on the commitments I make.'],
    ['Accountability', 'I set clear ownership and deadlines.'],
    ['Accountability', 'I address missed expectations directly and respectfully.'],
    ['Accountability', 'I hold myself to the standards I expect from others.'],
    ['Accountability', 'I review progress consistently instead of waiting for problems.'],
    ['Emotional intelligence', 'I remain steady when pressure is high.'],
    ['Emotional intelligence', 'I recognize how my emotions affect the team.'],
    ['Emotional intelligence', 'I respond with curiosity rather than defensiveness.'],
    ['Emotional intelligence', 'I notice when a team member needs support.'],
    ['Emotional intelligence', 'I adjust my approach after receiving feedback.'],
    ['Decision-making', 'I make timely decisions with the information available.'],
    ['Decision-making', 'I explain the reasoning behind important decisions.'],
    ['Decision-making', 'I involve the right people without avoiding responsibility.'],
    ['Decision-making', 'I separate urgent issues from important priorities.'],
    ['Decision-making', 'I learn from outcomes and adjust quickly.'],
    ['Team development', 'I coach people instead of solving every problem for them.'],
    ['Team development', 'I match responsibilities to strengths and growth goals.'],
    ['Team development', 'I recognize progress and meaningful contributions.'],
    ['Team development', 'I create opportunities for others to lead.'],
    ['Team development', 'I build trust by giving appropriate ownership.']
  ];

  const guidance = {
    'Communication': 'Create clarity by naming the outcome, owner, deadline, and next check-in.',
    'Accountability': 'Strengthen follow-through with specific commitments and consistent review.',
    'Emotional intelligence': 'Pause, notice your impact, and choose a response that protects trust.',
    'Decision-making': 'Clarify the decision, involve the right voices, then own the call.',
    'Team development': 'Coach with questions and give others meaningful ownership.'
  };

  const screens = {
    intro: root.querySelector('[data-screen="intro"]'),
    quiz: root.querySelector('[data-screen="quiz"]'),
    capture: root.querySelector('[data-screen="capture"]'),
    results: root.querySelector('[data-screen="results"]')
  };
  const questionText = root.querySelector('[data-question-text]');
  const categoryText = root.querySelector('[data-question-category]');
  const counter = root.querySelector('[data-counter]');
  const bar = root.querySelector('[data-progress]');
  const error = root.querySelector('[data-error]');
  const leadForm = root.querySelector('[data-lead-form]');
  const formError = root.querySelector('[data-form-error]');
  let index = 0;
  const answers = Array(questions.length).fill(null);
  const savedAnswers = sessionStorage.getItem('solLeadershipAssessmentAnswers');

  const recommendations = {
    foundations: {
      tag: 'leadership-recommendation-foundations',
      title: 'Leadership Foundations',
      summary: 'Build the core habits, mindset, and practical skills that make leadership clear, consistent, and trusted.',
      cta: 'Explore Leadership Foundations',
      url: '/products/leadership-foundations'
    },
    conversations: {
      tag: 'leadership-recommendation-difficult-conversations',
      title: 'Difficult Conversations',
      summary: 'Strengthen the communication and accountability skills needed to address hard issues with clarity, confidence, and respect.',
      cta: 'Explore Difficult Conversations',
      url: '/products/difficult-conversations'
    },
    coaching: {
      tag: 'leadership-recommendation-coaching',
      title: 'Discovery Coaching Session',
      summary: 'Turn your assessment insights into a focused action plan with personalized leadership coaching.',
      cta: 'Book a Discovery Coaching Session',
      url: 'https://calendly.com/southonleadership/discovery-coaching'
    }
  };

  const calculateProfile = () => {
    const categories = {};
    questions.forEach(([category], questionIndex) => {
      categories[category] ??= [];
      categories[category].push(answers[questionIndex]);
    });
    const total = answers.reduce((sum, score) => sum + score, 0);
    const percent = Math.round(((total - questions.length) / (questions.length * 4)) * 100);
    const categoryScores = Object.fromEntries(Object.entries(categories).map(([name, values]) => [
      name,
      Math.round(((values.reduce((a, b) => a + b, 0) - values.length) / (values.length * 4)) * 100)
    ]));
    const weakestCategory = Object.entries(categoryScores).sort((a, b) => a[1] - b[1])[0][0];
    let recommendation = recommendations.foundations;
    if (percent >= 60 && ['Communication', 'Accountability'].includes(weakestCategory)) {
      recommendation = recommendations.conversations;
    } else if (percent >= 60) {
      recommendation = recommendations.coaching;
    }
    return { categories, percent, categoryScores, weakestCategory, recommendation };
  };

  const show = (screen) => {
    Object.values(screens).forEach((element) => element.classList.add('sol-hidden'));
    screen.classList.remove('sol-hidden');
    screen.focus({ preventScroll: true });
  };

  const scrollToAssessment = () => window.scrollTo({ top: root.offsetTop - 20, behavior: 'smooth' });

  const render = () => {
    const [category, text] = questions[index];
    categoryText.textContent = category;
    questionText.textContent = text;
    counter.textContent = `Question ${index + 1} of ${questions.length}`;
    bar.style.width = `${((index + 1) / questions.length) * 100}%`;
    root.querySelectorAll('[data-value]').forEach((button) => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.value) === answers[index]));
    });
    root.querySelector('[data-prev]').classList.toggle('sol-hidden', index === 0);
    root.querySelector('[data-next]').textContent = index === questions.length - 1 ? 'See my results' : 'Next question';
    error.textContent = '';
  };

  const renderResults = () => {
    const { categories, percent, recommendation } = calculateProfile();
    let title = 'Leadership foundation';
    let summary = 'You have identified clear opportunities to build stronger everyday leadership habits.';
    if (percent >= 80) {
      title = 'Leadership multiplier';
      summary = 'Your habits consistently create clarity, trust, accountability, and growth for others.';
    } else if (percent >= 60) {
      title = 'Growing leader';
      summary = 'You have a solid leadership base with a few focused opportunities for greater consistency.';
    } else if (percent >= 40) {
      title = 'Emerging leader';
      summary = 'Your leadership potential is visible. Focused practice will turn good intentions into dependable habits.';
    }
    root.querySelector('[data-score]').textContent = `${percent}%`;
    root.querySelector('[data-result-title]').textContent = title;
    root.querySelector('[data-result-summary]').textContent = summary;
    root.querySelector('[data-category-results]').innerHTML = Object.entries(categories).map(([name, values]) => {
      const score = Math.round(((values.reduce((a, b) => a + b, 0) - values.length) / (values.length * 4)) * 100);
      return `<div class="sol-category"><div class="sol-category__head"><span class="sol-category__name">${name}</span><strong>${score}%</strong></div><div class="sol-category__track"><div class="sol-category__bar" style="width:${score}%"></div></div><p>${guidance[name]}</p></div>`;
    }).join('');
    root.querySelector('[data-recommendation-title]').textContent = recommendation.title;
    root.querySelector('[data-recommendation-summary]').textContent = recommendation.summary;
    const recommendationLink = root.querySelector('[data-recommendation-link]');
    recommendationLink.href = recommendation.url;
    recommendationLink.textContent = recommendation.cta;
    show(screens.results);
    scrollToAssessment();
  };

  root.querySelector('[data-start]').addEventListener('click', () => {
    index = 0;
    show(screens.quiz);
    render();
  });
  root.querySelectorAll('[data-value]').forEach((button) => button.addEventListener('click', () => {
    answers[index] = Number(button.dataset.value);
    render();
  }));
  root.querySelector('[data-prev]').addEventListener('click', () => {
    if (index > 0) {
      index -= 1;
      render();
    }
  });
  root.querySelector('[data-next]').addEventListener('click', () => {
    if (answers[index] === null) {
      error.textContent = 'Choose one response before continuing.';
      return;
    }
    if (index < questions.length - 1) {
      index += 1;
      render();
    } else {
      show(screens.capture);
      scrollToAssessment();
    }
  });
  leadForm.addEventListener('submit', () => {
    formError.textContent = '';
    sessionStorage.setItem('solLeadershipAssessmentAnswers', JSON.stringify(answers));
    const { recommendation } = calculateProfile();
    leadForm.querySelector('[data-contact-tags]').value = `newsletter,leadership-assessment,${recommendation.tag}`;
    const currentUrl = new URL(window.location.href);
    const previewThemeId = currentUrl.searchParams.get('preview_theme_id');
    const returnTo = leadForm.querySelector('[data-return-to]');
    returnTo.value = `/pages/leadership-assessment?customer_posted=true${previewThemeId ? `&preview_theme_id=${encodeURIComponent(previewThemeId)}` : ''}`;
    const submit = leadForm.querySelector('[type="submit"]');
    submit.disabled = true;
    submit.textContent = 'Saving…';
  });
  root.querySelector('[data-retake]').addEventListener('click', () => {
    answers.fill(null);
    index = 0;
    show(screens.intro);
    scrollToAssessment();
  });
  root.querySelector('[data-print]').addEventListener('click', () => window.print());

  if (new URLSearchParams(window.location.search).get('customer_posted') === 'true' && savedAnswers) {
    try {
      const restoredAnswers = JSON.parse(savedAnswers);
      if (Array.isArray(restoredAnswers) && restoredAnswers.length === questions.length) {
        restoredAnswers.forEach((value, answerIndex) => { answers[answerIndex] = value; });
        sessionStorage.removeItem('solLeadershipAssessmentAnswers');
        renderResults();
      }
    } catch (restoreError) {
      sessionStorage.removeItem('solLeadershipAssessmentAnswers');
    }
  }
});
